import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SCRIPT_IMG, ScriptImage } from "@/components/ScriptImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RSVPForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    attendance: "",
    dietary_requirements: "",
    transport_required: "n/a",
    song_requests: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.attendance) {
      toast.error("Please fill in your name, email and attendance.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        attendance: form.attendance,
        dietary_requirements: form.dietary_requirements.trim(),
        transport_required:
          form.attendance === "yes" ? form.transport_required : "n/a",
        song_requests: form.song_requests.trim(),
      });
      setSubmitted(true);
      toast.success("Thank you — your RSVP is in.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err.message || "Something went wrong";
      toast.error(
        typeof msg === "string" ? msg : "Could not save your RSVP. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="rsvp-thanks"
        className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6"
      >
        <ScriptImage src={SCRIPT_IMG.thankYou} alt="thank you" className="mb-6" />
        <p
          className="text-base md:text-lg uppercase tracking-[0.28em]"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2C1A1D" }}
        >
          Your reply has been received.
        </p>
        <p
          className="mt-4 text-xs uppercase tracking-[0.3em] text-[#7A6366]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          we cannot wait to celebrate with you!
        </p>
      </div>
    );
  }

  const labelCls =
    "block text-[11px] uppercase tracking-[0.32em] mb-2 text-[#76232F]";
  const inputCls =
    "w-full bg-transparent border-0 border-b border-[#76232F]/30 focus:border-[#76232F] focus:outline-none px-0 py-2 text-[#2C1A1D] text-base placeholder:text-[#7A6366]/50";

  return (
    <form
      data-testid="rsvp-form"
      onSubmit={onSubmit}
      className="max-w-[640px] mx-auto px-6 md:px-0 py-10 space-y-9"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <div
        className="text-center text-sm md:text-base text-[#2C1A1D]/80 mb-2 space-y-2"
        data-testid="rsvp-deadline"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        <p>
          We kindly ask that you RSVP by no later than{" "}
          <strong className="font-semibold text-[#2C1A1D]">
            5th of September
          </strong>
          .
        </p>
        <p className="italic text-[#2C1A1D]/70 text-[13px] md:text-[14px]">
          Please note, we are only able to accommodate named guests on your
          invitation.
        </p>
      </div>

      <div>
        <label className={labelCls} htmlFor="rsvp-name">Full Name</label>
        <input
          id="rsvp-name"
          data-testid="rsvp-name"
          type="text"
          value={form.full_name}
          onChange={set("full_name")}
          required
          className={inputCls}
          placeholder="e.g. Jane &amp; John Smith"
        />
      </div>

      <div>
        <label className={labelCls} htmlFor="rsvp-email">Email</label>
        <input
          id="rsvp-email"
          data-testid="rsvp-email"
          type="email"
          value={form.email}
          onChange={set("email")}
          required
          className={inputCls}
          placeholder="you@example.com"
        />
      </div>

      <div>
        <span className={labelCls}>Will you attend?</span>
        <div className="flex gap-8 mt-1">
          {[
            { v: "yes", l: "Graciously accepts" },
            { v: "no", l: "Respectfully declines" },
          ].map((opt) => (
            <label
              key={opt.v}
              className="flex items-center gap-3 cursor-pointer text-[#2C1A1D] text-base"
            >
              <input
                type="radio"
                name="attendance"
                value={opt.v}
                checked={form.attendance === opt.v}
                onChange={set("attendance")}
                data-testid={`rsvp-attendance-${opt.v}`}
                className="accent-[#76232F]"
              />
              {opt.l}
            </label>
          ))}
        </div>
      </div>

      {form.attendance === "yes" && (
        <div data-testid="rsvp-yes-block" className="space-y-9">
          <div>
            <label className={labelCls} htmlFor="rsvp-dietary">
              Dietary requirements
            </label>
            <input
              id="rsvp-dietary"
              data-testid="rsvp-dietary"
              type="text"
              value={form.dietary_requirements}
              onChange={set("dietary_requirements")}
              className={inputCls}
              placeholder="Allergies, intolerances, preferences…"
            />
          </div>

          <div data-testid="rsvp-transport-block">
            <span className={labelCls}>
              If staying in Ravensbourne or Toowoomba, do you require transport to and from the venue?
            </span>
            <div className="flex gap-8 mt-1">
              {[
                { v: "yes", l: "Yes, please" },
                { v: "no", l: "Not required" },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className="flex items-center gap-3 cursor-pointer text-[#2C1A1D] text-base"
                >
                  <input
                    type="radio"
                    name="transport_required"
                    value={opt.v}
                    checked={form.transport_required === opt.v}
                    onChange={set("transport_required")}
                    data-testid={`rsvp-transport-${opt.v}`}
                    className="accent-[#76232F]"
                  />
                  {opt.l}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="rsvp-songs">
              Song requests
            </label>
            <input
              id="rsvp-songs"
              data-testid="rsvp-songs"
              type="text"
              value={form.song_requests}
              onChange={set("song_requests")}
              className={inputCls}
              placeholder="What will get you on the dance floor?"
            />
          </div>
        </div>
      )}

      {form.attendance === "no" && (
        <div>
          <label className={labelCls} htmlFor="rsvp-note">
            A note for the couple
          </label>
          <input
            id="rsvp-note"
            data-testid="rsvp-note"
            type="text"
            value={form.song_requests}
            onChange={set("song_requests")}
            className={inputCls}
            placeholder="A few words you&rsquo;d like us to read…"
          />
        </div>
      )}

      <div className="pt-4">
        <button
          type="submit"
          data-testid="rsvp-submit"
          disabled={submitting}
          className="px-10 py-3 border border-[#76232F] text-[#76232F] text-xs uppercase tracking-[0.32em] hover:bg-[#76232F] hover:text-[#F4EBDD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending…" : "Send reply"}
        </button>
      </div>
    </form>
  );
}
